import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableRevenueStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportrevenueStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchRevenueReportStation,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setRevenueStationReportUpdate,
  setSortData,
  refreshRevenueReportStation,
} from '../../../../redux/slices/ReportRevenueStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * Revenue Report Table for Station
 * @param {any} properties - The properties
 */
function RevenueReportTableStation(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    sortBy,
    sortField,
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isRevenueStationReportUpdate,
    isFilterUpdated,
  } = useAppSelector((state) => state.reportRevenueStation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  /**
   * Fetches energy report data for the selected facility.
   * @param {any} data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchRevenueReportStation({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchRevenueReportStation({ ...queryParameters, page, limit, sortField, sortBy });
  };
  const { isEditMode, handleEditModeChange, handleSidePanelClose,renderSidePanelTopLeftContent } = useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: reports,
      disableEdit: true,
      disableDelete: true,
    });

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableRevenueStationReport,
      /**
       * S.No view
       * @param {any} report - The report
       * @param {any} index - The index
       */
      customData: (report: AvailableRevenueStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableRevenueStationReport,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableRevenueStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableRevenueStationReport,
      sortAllow: true,
    },
    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>{ToFixedValue(row?.totalRevenue)}</span>
      ),
    },
    {
      label: t('basePricingRevenue'),
      accessorKey: 'basePriceRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>{ToFixedValue(row?.basePriceRevenue)}</span>
      ),
    },
    {
      label: t('peakPricingRevenue'),
      accessorKey: 'peakPriceRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>{ToFixedValue(row?.peakPriceRevenue)}</span>
      ),
    },
    {
      label: t('offPeakPricingRevenue'),
      accessorKey: 'offPeakPriceRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>{ToFixedValue(row?.offPeakPriceRevenue)}</span>
      ),
    },
    {
      label: t('scheduleChargeRevenue'),
      accessorKey: 'scheduleChargeRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>{ToFixedValue(row?.scheduleChargeRevenue)}</span>
      ),
    },
  ];

  /**
   * Handles automatic refresh of the report by fetching data with the current filters and pagination.
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshRevenueReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        accountCustomId: reportsData?.accountId,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Current columns for Revenue Station Report export
   */
  const currentColumns = [
    { label: 'SNo', accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('basePricingRevenue'), accessorKey: 'basePriceRevenue' },
    { label: t('peakPricingRevenue'), accessorKey: 'peakPriceRevenue' },
    { label: t('offPeakPricingRevenue'), accessorKey: 'offPeakPriceRevenue' },
    { label: t('scheduleChargeRevenue'), accessorKey: 'scheduleChargeRevenue' },
    { label: 'Created', accessorKey: 'createdAt' },
    { label: 'Last Updated', accessorKey: 'updatedAt' },
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchRevenueStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableRevenueStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount || '0';

              case 'totalRevenue':
                return row?.totalRevenue ? ToFixedValue(row.totalRevenue) : '0.00';

              case 'basePriceRevenue':
                return row?.basePriceRevenue ? ToFixedValue(row.basePriceRevenue) : '0.00';

              case 'peakPriceRevenue':
                return row?.peakPriceRevenue ? ToFixedValue(row.peakPriceRevenue) : '0.00';

              case 'offPeakPriceRevenue':
                return row?.offPeakPriceRevenue ? ToFixedValue(row.offPeakPriceRevenue) : '0.00';

              case 'scheduleChargeRevenue':
                return row?.scheduleChargeRevenue
                  ? ToFixedValue(row.scheduleChargeRevenue)
                  : '0.00';

              case 'createdAt':
                return (row as any).createdAt ? ConvertUTCtoDateTime((row as any).createdAt) : '-';

              case 'updatedAt':
                return (row as any).updatedAt ? ConvertUTCtoDateTime((row as any).updatedAt) : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      year: reportsData.reportFilter.year,
      page,
      limit,
    });
  };
  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    });
  };
  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      year: reportsData.reportFilter.year,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isRevenueStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setRevenueStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isRevenueStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'revenueReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
        accountCustomId: reportsData?.accountId,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={370}
          created
          lastUpdated
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          refershCurrentPages={ReferenceCurrentPages}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          sizeChangeButtonRequired
          onHiddenColumnsChange={setHiddenColumns}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        />
      </div>
    </div>
  );
}

export default RevenueReportTableStation;
